/** Shared social / SEO meta helpers for static HTML pages. */

export const SITE_ORIGIN = 'https://calendarify.online';
export const OG_IMAGE_URL = `${SITE_ORIGIN}/og-image.jpg`;
export const OG_IMAGE_ALT = 'Calendarify — free printable calendars online';

type MetaKind = 'name' | 'property';

function upsertMeta(kind: MetaKind, key: string, content: string): void {
  const selector = kind === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, attrs?: Record<string, string>): void {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  if (attrs) {
    for (const [name, value] of Object.entries(attrs)) {
      el.setAttribute(name, value);
    }
  }
}

export interface PageSocialMeta {
  title: string;
  description: string;
  /** Absolute page URL for og:url / canonical */
  url: string;
  /** twitter:card type */
  twitterCard?: 'summary' | 'summary_large_image';
}

/** Apply title, description, Open Graph, and X/Twitter card tags. */
export function applySocialMeta(meta: PageSocialMeta): void {
  const { title, description, url, twitterCard = 'summary_large_image' } = meta;

  document.title = title;

  upsertMeta('name', 'description', description);

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', 'Calendarify');
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', OG_IMAGE_URL);
  upsertMeta('property', 'og:image:alt', OG_IMAGE_ALT);

  upsertMeta('name', 'twitter:card', twitterCard);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE_URL);
  upsertMeta('name', 'twitter:image:alt', OG_IMAGE_ALT);

  upsertLink('canonical', url);
}

export function absoluteUrl(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${SITE_ORIGIN}${path}`;
}
